import { ExclamationIcon } from '@heroicons/react/solid';
import { useAtom, useAtomValue } from 'jotai';
import {
  ChangeEvent,
  FocusEvent,
  memo,
  useCallback,
  useEffect,
  useRef,
  useTransition,
} from 'react';
import { RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Alert, Checkbox, Flex, IconSvg, InputTextArea, Text } from '@/lib/v2/components';

import { CountSMS } from '@/src/modules/CampaignsModule/components/actionManagement/SmsAction/components/SmsForm/components/CountSMS';
import { ISMSFormInfo } from '@/src/modules/CampaignsModule/components/actionManagement/SmsAction/components/SmsForm/hooks/useSmsForm';
import { useSmsFormEvents } from '@/src/modules/CampaignsModule/components/actionManagement/SmsAction/components/SmsForm/hooks/useSmsFormEvents';
import { MAX_LENGTH_SMS } from '@/src/modules/CampaignsModule/components/actionManagement/SmsAction/constants';
import { replaceUrlsWithShortLinks } from '@/src/modules/CampaignsModule/components/actionManagement/SmsAction/utils';

import { atomGlobalLoadingAction } from '@/modules/CampaignsModule/atoms/actionEmailAtom';
import {
  atomActionInfoSms,
  atomInfoSmsPayload,
  atomMessageSMS,
} from '@/modules/CampaignsModule/atoms/actionSMSAtom';
import { atomActionData, atomActionInitialize } from '@/modules/CampaignsModule/atoms/campaignAtom';
import { SubjectButtons } from '@/modules/CampaignsModule/components/SubjectButtons';
import {
  EButtonType,
  ISubjectChangeButtonsProps,
} from '@/modules/CampaignsModule/components/SubjectButtons/SubjectButtons.interface';
import { TRANSLATE_SMS_ACTION_FORM } from '@/modules/CampaignsModule/constants';
import { IFormProps, ISmsInfoPayload } from '@/modules/CampaignsModule/interfaces/CampaignActions';

interface IMessageInputProps {
  loading?: boolean;
  form: IFormProps<ISMSFormInfo>;
  rules: Record<string, RegisterOptions>;
  disabled?: boolean;
}

const MessageInput = ({ loading, form, rules, disabled = false }: IMessageInputProps) => {
  const { t } = useTranslation();
  const { actionId } = useParams();
  const [messageSMS, setMessageSMS] = useAtom(atomMessageSMS);
  const [infoSmsPayload, setInfoSmsPayload] = useAtom(atomInfoSmsPayload);
  const actionInfoSms = useAtomValue(atomActionInfoSms);
  // cspell:disable
  const [action] = useAtom(atomActionData);
  // cspell:enable
  const [isInitialized, setIsInitialized] = useAtom(atomActionInitialize);
  const { setInfoOnEvent } = useSmsFormEvents({ actionId: Number(actionId) });
  const [globalLoadingAction, setGlobalLoadingAction] = useAtom(atomGlobalLoadingAction);
  const [isPending, startTransition] = useTransition();

  const { control, setValue } = form;

  const messageRef = useRef<HTMLTextAreaElement>(null);

  const handleChangeMessage = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = event.target;
      setMessageSMS({ ...messageSMS, original: value });
    },
    [messageSMS, setMessageSMS]
  );

  /* cspell:disable */
  const handleBlurMessage = useCallback(
    (event: FocusEvent<HTMLTextAreaElement>) => {
      const { value } = event.target;

      const payload: ISmsInfoPayload = { ...infoSmsPayload, mensaje: value };

      setInfoOnEvent(payload);
      setInfoSmsPayload(payload);
    },
    [infoSmsPayload, setInfoOnEvent, setInfoSmsPayload]
  );
  /* cspell:enable */

  /* cspell:disable */
  const handleChangeSubjectButton = useCallback(
    ({ item }: ISubjectChangeButtonsProps) => {
      const currentMessage = messageSMS.original;
      const message = currentMessage ? `${currentMessage} ${item}` : item;
      setValue('message', message);
      setMessageSMS({ ...messageSMS, original: message });

      const payload: ISmsInfoPayload = { ...infoSmsPayload, mensaje: message };

      setInfoOnEvent(payload);
      setInfoSmsPayload(payload);
    },
    [setValue, setMessageSMS, messageSMS, infoSmsPayload, setInfoOnEvent, setInfoSmsPayload]
  );
  /* cspell:enable */

  const applyShortenerUrl = useCallback(
    (withShort = false, currentSMS = messageSMS.original) => {
      const shortenedSMS = replaceUrlsWithShortLinks(currentSMS);

      setValue('message', withShort ? shortenedSMS : currentSMS);

      const payload: ISmsInfoPayload = { ...infoSmsPayload, track: !!withShort };

      setInfoOnEvent(payload);
      setInfoSmsPayload(payload);
      setMessageSMS({
        ...messageSMS,
        original: currentSMS,
        shortened: withShort ? shortenedSMS : '',
        track: !!withShort,
      });
    },
    [infoSmsPayload, messageSMS, setInfoOnEvent, setInfoSmsPayload, setMessageSMS, setValue]
  );

  const handleChangeShortUrl = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { checked } = event.target;
      applyShortenerUrl(checked);
    },
    [applyShortenerUrl]
  );
  /* cspell:disable */
  useEffect(() => {
    if ((actionId && action?.actionId !== +actionId) || loading) return;

    const actionAvailable = Object.values(actionInfoSms).length > 0;
    const message = actionInfoSms?.mensaje ?? '';

    if (!actionAvailable || !action || isInitialized) return;

    startTransition(() => {
      applyShortenerUrl(action.trackingUrl, message);
      setGlobalLoadingAction(false);
      !isInitialized && setIsInitialized(true);
    });
  }, [
    action,
    loading,
    isInitialized,
    actionInfoSms,
    applyShortenerUrl,
    setIsInitialized,
    setGlobalLoadingAction,
    actionId,
  ]);
  /* cspell:enable */

  return (
    <div>
      <Text
        className="sticky top-0 z-[1] border-b border-solid border-emblue-borders bg-emblue-white pb-2"
        color="primary"
        fontWeight="medium"
        variant="sub-headline"
      >
        {t(`${TRANSLATE_SMS_ACTION_FORM}.message`)}
      </Text>
      <Text className="mt-4" color="dark" fontWeight="normal" variant="text">
        {t(`${TRANSLATE_SMS_ACTION_FORM}.messageDisclaimer`)}
      </Text>
      <div className="mb-10">
        <Flex withGap className="mt-9" gapSize="small" itemAlignment="start" textAlignment="center">
          <div className="-mt-1 w-full">
            <InputTextArea
              ref={messageRef}
              control={control}
              disabled={disabled || messageSMS.track}
              id="sms_message"
              isLoading={loading}
              name="message"
              rows={7}
              rules={rules.message}
              onBlur={handleBlurMessage}
              onChange={handleChangeMessage}
            />
            {!loading && (
              <div
                className={`flex gap-1 ${messageSMS.count > 1 ? 'justify-between' : 'justify-end'}`}
              >
                {messageSMS.count > 1 && (
                  <Text className="mt-1 flex w-4/5 gap-2" fontWeight="normal" variant="text">
                    <IconSvg
                      className="mt-0.5 min-w-[20px]"
                      fillColor="passive"
                      height="20px"
                      svgComponent={<ExclamationIcon />}
                      width="20px"
                    />
                    {t(`${TRANSLATE_SMS_ACTION_FORM}.messageDisclaimerLength`)}
                  </Text>
                )}
                <CountSMS
                  count={messageSMS.count}
                  length={messageSMS.length}
                  maxLength={MAX_LENGTH_SMS}
                />
              </div>
            )}
          </div>
          <SubjectButtons
            vertical
            actionId={Number(actionId)}
            disabled={disabled || messageSMS.track}
            exclude={[EButtonType.TAG, EButtonType.EMOJI]}
            id="sms_form-buttons"
            searchPlaceholder={t(`${TRANSLATE_SMS_ACTION_FORM}.searchDropdown`)}
            onChangeButtons={handleChangeSubjectButton}
          />
        </Flex>

        {messageSMS?.customFields > 0 && (
          <Alert
            className="mt-4"
            description={t(`${TRANSLATE_SMS_ACTION_FORM}.messageDisclaimerCustomFields`)}
            type="info"
          />
        )}
      </div>
      <div className="mb-10 flex flex-col">
        <Checkbox
          checked={messageSMS.track}
          control={control}
          disabled={loading || disabled}
          id="sms_short-track"
          label={t(`${TRANSLATE_SMS_ACTION_FORM}.shortener`)}
          onChange={handleChangeShortUrl}
        />
        <Text className="pl-5" color="dark" fontWeight="normal" variant="text">
          {t(`${TRANSLATE_SMS_ACTION_FORM}.shortenerTracking`)}
        </Text>
      </div>
    </div>
  );
};

export default memo(MessageInput);
